import { Icon, Icons } from 'components/Icon';
import React, { useEffect, useMemo, useState } from 'react';
import TextField from 'components/Form/TextField';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from 'components/Form/Button';
import CustomSelect from 'components/Form/CustomSelect';
import { BeneficiaryTypeEnum } from 'apps/user/types/user.enum.type';
import { UpdateExecutorType } from 'apps/user/types/on-boarding.type';
import {
  useGetExecutor,
  useUpdateExecutor
} from 'common/queries-and-mutations/executor';
import { toast } from 'react-toastify';
import { Alert } from 'components/Toast';
import Spacer from 'components/Spacer';
import { RelationshipType } from 'constants/bank-account-type';
import { InfoModal } from 'components/Modal';

type Props = {
  onClose: () => void;
  onSuccess: () => void;
  executorId: number;
};

export default function EditExecutorsModal({
  onClose,
  executorId,
  onSuccess
}: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutate, isSuccess, isLoading, isError } = useUpdateExecutor();

  const { data, isFetched } = useGetExecutor({
    executorId
  });

  const executor = useMemo(() => {
    if (isFetched && data) {
      return {
        executorFirstName: data.executorFirstName,
        executorMiddleName: data.executorMiddleName,
        executorLastName: data.executorLastName?.trim(),
        executorType: data.executorType,
        relationship: data.relationship,
        address: data.address,
        emailAddress: data.emailAddress,
        phoneNumber: data.phoneNumber,
        rcNumber: data.rcNumber,
        incorporationDate: data.incorporationDate,
        incorporationPlace: data.incorporationPlace
      };
    } else {
      return {
        executorFirstName: '',
        executorMiddleName: '',
        executorLastName: '',
        executorType: '',
        relationship: '',
        address: '',
        emailAddress: '',
        phoneNumber: '',
        rcNumber: '',
        incorporationDate: '',
        incorporationPlace: ''
      };
    }
  }, [data, isFetched]);

  const formik = useFormik({
    initialValues: { ...executor },
    validationSchema: Yup.object({
      executorFirstName: Yup.string().when('executorType', {
        is: BeneficiaryTypeEnum.INDIVIDUAL,
        then: Yup.string()
          .required('Executor first name is required')
          .nullable(),
        otherwise: Yup.string().required('Name is required').nullable()
      }),
      executorLastName: Yup.string().when('executorType', {
        is: BeneficiaryTypeEnum.INDIVIDUAL,
        then: Yup.string()
          .required('Executor last name is required')
          .nullable(),
        otherwise: Yup.string()
      }),
      executorType: Yup.string()
        .required('Executor type is required')
        .nullable(),
      address: Yup.string().required('Address is required').nullable(),
      emailAddress: Yup.string()
        .email('Invalid email')
        .required('Email is required')
        .nullable(),
      rcNumber: Yup.string().when('executorType', {
        is: BeneficiaryTypeEnum.CORPORATE,
        then: Yup.string().required('RC Number is required').nullable(),
        otherwise: Yup.string()
      }),
      phoneNumber: Yup.string().when('executorType', {
        is: BeneficiaryTypeEnum.INDIVIDUAL,
        then: Yup.string().required('Phone number is required').nullable(),
        otherwise: Yup.string()
      }),
      relationship: Yup.string().when('executorType', {
        is: BeneficiaryTypeEnum.INDIVIDUAL,
        then: Yup.string().required('Relationship is required').nullable(),
        otherwise: Yup.string()
      })
    }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onExecutorSubmit(values);
    }
  });

  const onExecutorSubmit = (formValue: UpdateExecutorType) => {
    mutate({ executorId, update: formValue });
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      toast(<Alert message="Executor Successfully Updated" type="success" />);
      onSuccess();
      setIsSubmitting(false);
    }
    if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError]);

  return (
    <InfoModal
      width="w-[55%]"
      className="h-fit rounded-md px-4 pb-9 pt-5 sm:p-9"
    >
      <div>
        <div className="flex flex-row items-center justify-between pl-6">
          <p className="text-2xl font-bold text-[#353535]">Edit Executor</p>
          <button type="button" onClick={onClose}>
            <Icon name={Icons.Close} />
          </button>
        </div>
        <form
          autoComplete="false"
          className="mt-10 px-6"
          onSubmit={formik.handleSubmit}
        >
          <div className="mb-1 mt-8">
            <div className="grid md:grid-cols-2 md:gap-10">
              <div className="group relative z-0 mb-6 w-full">
                <TextField
                  name={`executorFirstName`}
                  placeholder=""
                  value={formik.values.executorFirstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label={
                    formik.values.executorType ===
                    BeneficiaryTypeEnum.INDIVIDUAL
                      ? 'Executor First Name'
                      : 'Name'
                  }
                  error={
                    formik.touched.executorFirstName
                      ? formik.errors.executorFirstName
                      : ''
                  }
                  isRequired
                  size="lg"
                  className="rounded-2xl border-[1.5px] border-gray-300"
                />
              </div>
              <div className="group relative z-0 mb-6 w-full">
                <div className="relative mb-2 flex items-center justify-between">
                  <span className="flex items-center">
                    <label className="text-[12px] font-medium text-[#353535]">
                      Type of Executor
                    </label>
                    <div className="mx-2 mb-2">
                      <Spacer width={3} />
                      <Icon name={Icons.Required} />
                    </div>
                  </span>
                </div>
                <div className="mt-5 flex items-center gap-10">
                  <button
                    type="button"
                    onClick={() =>
                      formik.setFieldValue(
                        `executorType`,
                        BeneficiaryTypeEnum.INDIVIDUAL
                      )
                    }
                    className="flex items-center gap-3"
                  >
                    <span className="text-base font-bold text-[#353535]">
                      Individual
                    </span>
                    <Icon
                      name={
                        formik.values.executorType ===
                        BeneficiaryTypeEnum.INDIVIDUAL
                          ? Icons.CheckedBox
                          : Icons.UncheckBox
                      }
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      formik.setFieldValue(
                        `executorType`,
                        BeneficiaryTypeEnum.CORPORATE
                      )
                    }
                    className="flex items-center gap-3"
                  >
                    <span className="text-base font-bold text-[#353535]">
                      Corporate
                    </span>
                    <Icon
                      name={
                        formik.values.executorType ===
                        BeneficiaryTypeEnum.CORPORATE
                          ? Icons.CheckedBox
                          : Icons.UncheckBox
                      }
                    />
                  </button>
                </div>
              </div>
            </div>
            {formik.values.executorType === BeneficiaryTypeEnum.INDIVIDUAL && (
              <div className="grid md:grid-cols-2 md:gap-10">
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`executorMiddleName`}
                    placeholder=""
                    value={formik.values.executorMiddleName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Executor Middle Name"
                    error={
                      formik.touched.executorMiddleName
                        ? formik.errors.executorMiddleName
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`executorLastName`}
                    placeholder=""
                    value={formik.values.executorLastName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Executor Last Name"
                    error={
                      formik.touched.executorLastName
                        ? formik.errors.executorLastName
                        : ''
                    }
                    isRequired
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
              </div>
            )}
            {formik.values.executorType === BeneficiaryTypeEnum.INDIVIDUAL && (
              <div className="grid md:grid-cols-2 md:gap-10">
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`address`}
                    placeholder=""
                    isRequired
                    value={executor.address}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Home Address"
                    error={formik.touched.address ? formik.errors.address : ''}
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <CustomSelect
                    options={RelationshipType}
                    selectedOption={{
                      value: formik.values.relationship,
                      label: formik.values.relationship
                    }}
                    handleOptionChange={option => {
                      formik.setFieldValue(`relationship`, option?.value);
                    }}
                    label="Relationship"
                    name="relationship"
                    isRequired
                    isSearchable
                    className="h-[3.5rem] rounded-2xl"
                    error={
                      formik.touched.relationship
                        ? formik.errors.relationship
                        : ''
                    }
                  />
                </div>
              </div>
            )}
            {formik.values.executorType === BeneficiaryTypeEnum.CORPORATE && (
              <div className="grid md:grid-cols-2 md:gap-10">
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`rcNumber`}
                    placeholder=""
                    value={formik.values.rcNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="RC Number"
                    isRequired
                    error={
                      formik.touched.rcNumber ? formik.errors.rcNumber : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`address`}
                    placeholder=""
                    isRequired
                    value={formik.values.address}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Company Address"
                    error={formik.touched.address ? formik.errors.address : ''}
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
              </div>
            )}
            <div className="grid md:grid-cols-2 md:gap-10">
              <div className="group relative z-0 mb-6 w-full">
                {executor.executorType && (
                  <TextField
                    name={`emailAddress`}
                    placeholder=""
                    isRequired
                    value={formik.values.emailAddress}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Email Address"
                    error={
                      formik.touched.emailAddress
                        ? formik.errors.emailAddress
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                )}
              </div>
              <div className="group relative z-0 mb-6 w-full">
                {formik.values.executorType ===
                  BeneficiaryTypeEnum.INDIVIDUAL && (
                  <TextField
                    name={`phoneNumber`}
                    isRequired
                    placeholder=""
                    value={formik.values.phoneNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Phone Number"
                    error={
                      formik.touched.phoneNumber
                        ? formik.errors.phoneNumber
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                )}
                {formik.values.executorType ===
                  BeneficiaryTypeEnum.CORPORATE && (
                  <TextField
                    name={`incorporationDate`}
                    type="date"
                    placeholder=""
                    value={formik.values.incorporationDate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Date of Incorporation"
                    error={
                      formik.touched.incorporationDate
                        ? formik.errors.incorporationDate
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                )}
              </div>
            </div>
            <div className="grid md:grid-cols-2 md:gap-10">
              <div className="group relative z-0 mb-6 w-full">
                {formik.values.executorType ===
                  BeneficiaryTypeEnum.CORPORATE && (
                  <TextField
                    name={`incorporationPlace`}
                    placeholder=""
                    value={formik.values.incorporationPlace}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Place of Incorporation"
                    error={
                      formik.touched.incorporationPlace
                        ? formik.errors.incorporationPlace
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2">
            <div className="flex w-full items-center gap-2">
              <Icon name={Icons.Required} />
              <span className="text-[10px] text-[#7A7C87]">
                Indicates Required
              </span>
            </div>
          </div>

          <div className="mt-5 flex items-end justify-end gap-8">
            <Button
              variant="primary"
              size="md"
              className="mt-5 w-[9rem] rounded p-[0.1rem]"
              label="Update Executor"
              type="submit"
              disabled={!formik.isValid}
              loading={isSubmitting || isLoading}
            ></Button>
          </div>
        </form>
      </div>
    </InfoModal>
  );
}
